import * as React from "react";
import {makeStyles, Theme, useTheme} from '@material-ui/core/styles';
import {useTranslation} from 'react-i18next';
import {IData as ContactpointListData} from '../../../store/contactpoint/contactpointList';
import {DopTable, IColumnDefinition, RowState} from '../../organisms/DopTable';
import {DetailsButton} from '../../atoms/buttons/DetailsButton';
import {EditButton} from '../../atoms/buttons/EditButton';

// All the incoming values
interface IProps {
    title: string;
    data: ContactpointListData[];
    loading: boolean;
    hasError: boolean;
    errorDetails: string;
}

// Outgoing functions
interface IDispatch {
    onClickDetails: (id: string) => void;
    onClickEdit: (id: string) => void;
    onRefresh: () => void;
}

// CSS
const useStyles = makeStyles((theme: Theme) => ({ }));

// Actual Component
export type AllProps = IProps & IDispatch;
export const ContactpointList = (props: AllProps) => {
    const classes = useStyles(useTheme());
    const { t: tCommon } = useTranslation('common');
    const { t: tDatatypes } = useTranslation('datatypes');
    const { t: tContactpoint } = useTranslation('contactpoint');

    const columns: IColumnDefinition<ContactpointListData>[] = [
        { 
            title: tDatatypes('naamLabel'), 
            align: "left", 
            renderer: (value) => value.naam,
            sorter: (a, b) => a.naam.localeCompare(b.naam),
            searchable: true
        },
        { 
            title: tDatatypes('adresLabel'), 
            align: "left", 
            renderer: (value) => value.adress || '-',
            sorter: (a, b) => (a.adress || '').localeCompare(b.adress || ''),
            searchable: true
        },
        { 
            title: tDatatypes('stadLabel'), 
            align: "left",
            renderer: (value) => value.city || '-',
            sorter: (a, b) => (a.city || '').localeCompare(b.city || ''),
            searchable: true
        },
        { 
            title: tDatatypes('regioLabel'), 
            align: "left", 
            renderer: (value) => value.region || '-',
            sorter: (a, b) => (a.region || '').localeCompare(b.region || ''),
            searchable: true
        },
        { 
            title: tCommon('actions'), 
            align: "right", 
            renderer: (value) => (
                <>
                    <DetailsButton onClick={() => props.onClickDetails(value.id)} />
                    <EditButton onClick={() => props.onClickEdit(value.id)} />
                </>
            ),
            searchable: false // Don't search in action buttons
        },
    ]

    // Map contactpoint data to include status property for DopTable
    const rowsWithStatus = props.data.map(contactpoint => ({
        ...contactpoint,
        status: contactpoint.active ? 'normal' as const : 'inactive' as const
    }));

    return (<DopTable 
                data={{
                    rows: rowsWithStatus,
                    columns: columns,
                }}
                toolbar={{
                    title: props.title,
                    onRefresh: props.onRefresh,
                    enableBuiltinSearch: true,
                    searchPlaceholder: tContactpoint('searchPlaceholder')
                }}
                loading={props.loading}
                hasError={props.hasError}
                errorDetails={props.errorDetails}
                onClickRow={(row) => props.onClickDetails(row.id)}
                getRowState={(row) => row.active ? RowState.Normal : RowState.Inactive}
            />)
}
